import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Intervalo de checagem de nova versão enquanto o app fica aberto (PWA instalado
// costuma ficar dias sem recarregar).
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function PwaUpdatePrompt(): React.JSX.Element | null {
  const [offlineReadyShown, setOfflineReadyShown] = useState(false);
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setInterval(() => {
        void registration.update();
      }, UPDATE_CHECK_INTERVAL_MS);
      // Checa também ao voltar para a aba e quando Configurações pede.
      const check = (): void => { if (document.visibilityState === 'visible') void registration.update(); };
      document.addEventListener('visibilitychange', check);
      window.addEventListener('echo-check-update', check);
    },
  });

  // Versão nova pronta: aplica sozinha na próxima troca de tela, exceto no meio de um estudo
  // ou de uma prova, onde recarregar perderia o progresso. O botão continua disponível.
  useEffect(() => {
    if (!needRefresh) return;
    const applyWhenSafe = (): void => {
      const path = window.location.pathname;
      if (path.startsWith('/app/estudar') || path.startsWith('/app/prova')) return;
      void updateServiceWorker(true);
    };
    window.addEventListener('echo-route-changed', applyWhenSafe);
    return () => window.removeEventListener('echo-route-changed', applyWhenSafe);
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!offlineReady) return;
    setOfflineReadyShown(true);
    const timer = setTimeout(() => setOfflineReady(false), 4000);
    return () => clearTimeout(timer);
  }, [offlineReady, setOfflineReady]);

  if (!needRefresh && !offlineReady) return null;

  return (
    <div className="pwa-toast" role="status" aria-live="polite">
      {needRefresh ? (
        <>
          <span>Nova versão disponível.</span>
          <button type="button" onClick={() => void updateServiceWorker(true)}>
            Atualizar
          </button>
          <button type="button" className="pwa-toast-dismiss" onClick={() => setNeedRefresh(false)}>
            Agora não
          </button>
        </>
      ) : (
        offlineReadyShown && <span>Pronto para usar offline.</span>
      )}
    </div>
  );
}
